<template>
  <teleport to="body">
    <div v-if="show" ref="modalRoot" class="modal fade show" tabindex="-1" style="display: block;" role="dialog" aria-modal="true" :aria-label="title" @click.self="handleBackdropClick">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle-fill text-warning"></i> {{ title }}
            </h5>
            <button type="button" class="btn-close" @click="cancel"></button>
          </div>
          <div class="modal-body">
            <p class="mb-2"><strong>{{ message }}</strong></p>
            <p v-if="details" class="text-muted small mb-0">{{ details }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancel">
              Cancel
            </button>
            <button type="button" :class="['btn', confirmButtonClass]" @click="confirm">
              {{ confirmButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="show" class="modal-backdrop fade show"></div>
  </teleport>
</template>

<script setup>
import { ref } from 'vue';
import { useModalA11y } from '@/composables/useModalA11y';

const show = ref(false);
const resolvePromise = ref(null);
const modalRoot = ref(null);
useModalA11y(show, { rootRef: modalRoot, onEscape: () => cancel() });

// Props that will be set when showing the modal
const title = ref('');
const message = ref('');
const details = ref('');
const confirmButtonText = ref('Confirm');
const confirmButtonClass = ref('btn-primary');

function showModal(options) {
  title.value = options.title || 'Confirm';
  message.value = options.message || '';
  details.value = options.details || '';
  confirmButtonText.value = options.confirmButtonText || 'Confirm';
  confirmButtonClass.value = options.confirmButtonClass || 'btn-primary';

  show.value = true;

  return new Promise((resolve) => {
    resolvePromise.value = resolve;
  });
}

function confirm() {
  show.value = false;
  if (resolvePromise.value) {
    resolvePromise.value(true);
    resolvePromise.value = null;
  }
}

function cancel() {
  show.value = false;
  if (resolvePromise.value) {
    resolvePromise.value(false);
    resolvePromise.value = null;
  }
}

function handleBackdropClick() {
  cancel();
}

defineExpose({
  showModal
});
</script>

<style scoped>
.modal {
  background: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  margin: 1.75rem auto;
}

.modal-content {
  border-radius: var(--radius-lg);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
}

/* Sit above Bootstrap modals (z-index 1055) so confirmations triggered from
   within an open modal (e.g. the Edit Key Result dialog) are always visible. */
.modal-backdrop {
  z-index: 1065;
}

.modal {
  z-index: 1070;
}
</style>
